// adapted from: platform/packages/insights-ai-platform/ai-smart-button/src/ui/ai-actions/hooks/useFetchConvoStarters.ts
import React, { useEffect, useMemo, useRef } from 'react';

import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import type { ConvoStarterErrorType, ConvoStarterObject } from '@atlassian/convo-ai-api/convo-api';

import {
	type AIActionType,
	type ChatAction,
	HandleActionType,
	type PageContextType,
} from '../../common/types';
import { isPageContextTypeSupportedForDynamicStarters } from '../../common/utils/dynamic-starters-context-eligibility';
import { getContentAri } from '../../common/utils/extract-urls';

const mapToConversationActionNew = (starterObject: ConvoStarterObject): ChatAction => {
	return {
		id: 'convo-starter',
		title: starterObject.starter,
		icon: () => <AiChatIcon label="" />,
		staticPrompt: starterObject.starter,
		handleType: HandleActionType.Chat,
		starterType: 'dynamic',
		jtbdCategory: starterObject.jtbd_category,
		agent: undefined,
		type: 'convo-starter-action' as AIActionType,
	};
};

type UseFetchConvoStartersReturnType = {
	convoStarters: ChatAction[];
	error?: Error;
	isLoading: boolean;
};

export const useFetchConvoStarters: (
	props: Omit<
		AssistanceServiceInjectedParams & {
			contentUrl: string;
			pageContextType: PageContextType;
			showConvoStarters?: boolean;
		},
		'assistanceServiceInjectedParams'
	> &
		AssistanceServiceInputParam,
) => UseFetchConvoStartersReturnType = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { assistanceService, experienceTracker, siteId },
		contentUrl,
		pageContextType,
		showConvoStarters = false,
	}: AssistanceServiceInjectedParams & {
		contentUrl: string;
		pageContextType: PageContextType;
		showConvoStarters?: boolean;
	}): UseFetchConvoStartersReturnType => {
		const { sendAnalyticsEvent } = useAnalytics();
		const timingRef = useRef<{ startTime: number; sent: boolean; pageContextType: string } | null>(
			null,
		);

		const contentAri = useMemo(
			() => getContentAri(siteId, contentUrl, pageContextType),
			[siteId, contentUrl, pageContextType],
		);

		// TODO ASIMO-2398: experiment cleanup
		const urlDebugInfo = useMemo(() => {
			try {
				const url = new URL(contentUrl);
				return {
					pathSegmentCount: url.pathname.split('/').filter(Boolean).length,
					hasQueryParams: url.search.length > 0,
					protocol: url.protocol,
				};
			} catch {
				return {
					pathSegmentCount: 0,
					hasQueryParams: false,
					protocol: 'invalid',
				};
			}
		}, [contentUrl]);

		const isPageContextTypeSupported =
			isPageContextTypeSupportedForDynamicStarters(pageContextType);
		const isEligibleContext = isPageContextTypeSupported && contentAri !== null;
		const isFeatureEnabled = showConvoStarters && isEligibleContext;

		// TODO ASIMO-2398: experiment cleanup
		useEffect(() => {
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'called',
				actionSubject: 'useFetchConvoStarters',
				attributes: {
					showConvoStarters,
					isPageContextTypeSupported,
					isEligibleContext,
					isFeatureEnabled,
					pageContextType: String(pageContextType),
					hasContentAri: contentAri !== null,
					contentAri,
					urlStructure: urlDebugInfo,
				},
			});
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const {
			data: convoStarters,
			isFetching: isLoading,
			error: fetchError,
		} = useQuery<ChatAction[]>({
			enabled: isFeatureEnabled,
			queryKey: ['dynamicConvoStarters', contentAri ?? contentUrl],
			queryFn: async () => {
				// Custom performance tracking for dynamic starters experiment to analyze on Statsig.
				// Confluence ui taskSuccess events aren't sent to Statsig.
				timingRef.current = {
					startTime: performance.now(),
					sent: false,
					pageContextType: String(pageContextType),
				};

				experienceTracker.start({
					id: ExperienceName.FETCH_DYNAMIC_CONVERSATION_STARTERS,
					name: ExperienceName.FETCH_DYNAMIC_CONVERSATION_STARTERS,
				});

				sendAnalyticsEvent({
					eventType: 'track',
					action: 'started',
					actionSubject: 'fetchConvoStarters',
					attributes: {
						pageContextType: timingRef.current.pageContextType,
					},
				});

				const response = await assistanceService.getDynamicConvoStartersNew({
					ai_feature_input: {
						// safe to assert: query is disabled (enabled condition) when contentAri is null
						documents: [{ ari: contentAri as string }],
					},
				});

				// Since we only request one document, we should get exactly one result
				const result = response?.ai_feature_output?.results?.[0];

				if (!result) {
					throw new Error('Expected one result in API response but received none');
				}

				if (result.error) {
					const errorType: ConvoStarterErrorType = result.error.error_type;

					// expected failures (HYDRATION_ERROR) are silently filtered
					if (errorType === 'HYDRATION_ERROR') {
						return [];
					}

					throw new Error(`[${errorType}] ${result.error.message}`);
				}

				if (!result.convo_starters.length) {
					return [];
				}

				return result.convo_starters.map((starter) => mapToConversationActionNew(starter));
			},
			onSuccess: (data) => {
				experienceTracker.succeed({ name: ExperienceName.FETCH_DYNAMIC_CONVERSATION_STARTERS });
				if (timingRef.current && !timingRef.current.sent) {
					sendAnalyticsEvent({
						eventType: 'track',
						action: 'succeeded',
						actionSubject: 'fetchConvoStarters',
						attributes: {
							duration: performance.now() - timingRef.current.startTime,
							startersCount: data?.length ?? 0,
							pageContextType: timingRef.current.pageContextType,
						},
					});
					timingRef.current.sent = true;
				}
			},
			onError: (error) => {
				if (timingRef.current && !timingRef.current.sent) {
					sendAnalyticsEvent({
						eventType: 'track',
						action: 'failed',
						actionSubject: 'fetchConvoStarters',
						attributes: {
							duration: performance.now() - timingRef.current.startTime,
							errorMessage: (error as Error).message,
							pageContextType: timingRef.current.pageContextType,
						},
					});
					timingRef.current.sent = true;
				}
				experienceTracker.fail({
					teamName: 'Rovo Chat',
					name: ExperienceName.FETCH_DYNAMIC_CONVERSATION_STARTERS,
					error: error as Error,
				});
			},
		});

		// Track cancellations when component unmounts during fetch
		useEffect(() => {
			return () => {
				if (timingRef.current && !timingRef.current.sent) {
					sendAnalyticsEvent({
						eventType: 'track',
						action: 'cancelled',
						actionSubject: 'fetchConvoStarters',
						attributes: {
							duration: performance.now() - timingRef.current.startTime,
							pageContextType: timingRef.current.pageContextType,
						},
					});
					timingRef.current.sent = true;
				}
			};
		}, [sendAnalyticsEvent]);

		return useMemo(
			() => ({
				convoStarters: convoStarters ?? [],
				isLoading,
				error: fetchError ?? undefined,
			}),
			[convoStarters, isLoading, fetchError],
		);
	},
);
